"use strict";
import { createContext, useContext } from "react";
export function createHotkeyRegistryManager() {
  const entries = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  let nextId = 1;
  function notifyListeners() {
    const allEntries = Array.from(entries.values());
    listeners.forEach((listener) => listener(allEntries));
  }
  function generateId() {
    return `hotkey-${nextId++}-${Date.now()}`;
  }
  return {
    register(entry) {
      const id = generateId();
      const fullEntry = {
        ...entry,
        id,
        registeredAt: Date.now()
      };
      entries.set(id, fullEntry);
      notifyListeners();
      return () => {
        entries.delete(id);
        notifyListeners();
      };
    },
    getAll() {
      return Array.from(entries.values());
    },
    getByModule(moduleId) {
      return Array.from(entries.values()).filter((e) => e.moduleId === moduleId);
    },
    getByScope(scope) {
      return Array.from(entries.values()).filter((e) => e.scope === scope);
    },
    getByCombo(combo) {
      const normalizedCombo = combo.toLowerCase();
      return Array.from(entries.values()).filter((e) => e.combo.toLowerCase() === normalizedCombo);
    },
    getStats() {
      const allEntries = Array.from(entries.values());
      const byModule = {};
      const byScope = {};
      const comboScopeMap = /* @__PURE__ */ new Map();
      for (const entry of allEntries) {
        byModule[entry.moduleId] = (byModule[entry.moduleId] || 0) + 1;
        byScope[entry.scope] = (byScope[entry.scope] || 0) + 1;
        const key = `${entry.combo.toLowerCase()}:${entry.scope}`;
        const existing = comboScopeMap.get(key) || [];
        existing.push(entry);
        comboScopeMap.set(key, existing);
      }
      const conflicts = [];
      for (const [key, entryList] of comboScopeMap) {
        if (entryList.length > 1) {
          const [combo, scope] = key.split(":");
          conflicts.push({ combo, scope, entries: entryList });
        }
      }
      return {
        totalCount: allEntries.length,
        byModule,
        byScope,
        conflicts
      };
    },
    isRegistered(combo, scope) {
      const normalizedCombo = combo.toLowerCase();
      return Array.from(entries.values()).some(
        (e) => e.combo.toLowerCase() === normalizedCombo && e.scope === scope
      );
    },
    clearModule(moduleId) {
      let count = 0;
      for (const [id, entry] of entries) {
        if (entry.moduleId === moduleId) {
          entries.delete(id);
          count++;
        }
      }
      if (count > 0) {
        notifyListeners();
      }
      return count;
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(Array.from(entries.values()));
      return () => {
        listeners.delete(listener);
      };
    }
  };
}
export const HotkeyRegistryContext = createContext(null);
export function useShellHotkeyRegistry() {
  const registry = useContext(HotkeyRegistryContext);
  if (!registry) {
    throw new Error(
      "useShellHotkeyRegistry must be used within a HotkeyRegistryContext provider. Make sure the shell provides the HotkeyRegistryContext."
    );
  }
  return registry;
}
export function useShellHotkeys() {
  const registry = useShellHotkeyRegistry();
  return registry.getAll();
}
export function useShellHotkeyStats() {
  const registry = useShellHotkeyRegistry();
  return registry.getStats();
}
